import { useMemo } from 'react';

import {
  documentEndpoint,
  documentsEndpoint,
} from '@cd3p/core/constants/endpoints';
import {
  APIDocument,
  APIDocumentSearchResult,
  APILanguage,
  APISortOrder,
} from '@cd3p/core/types/api';
import { mapLanguageToApiLanguage } from '@cd3p/core/utils/docs';
import { httpMethod, simplifyBuilder } from '@cd3p/core/utils/sra';

import { bindActionCreators, useDispatch, useSelector } from 'third-party';

import { appSelectors } from 'selectors';

export const DIALOGS_PER_PAGE = 20;

export type DocumentsState = {
  items: APIDocumentSearchResult[];
  currentItem?: APIDocumentSearchResult | null;
  documentsLoaded: boolean;
};

const dialogsListState: DocumentsState = {
  items: [],
  currentItem: null,
  documentsLoaded: false,
};

const builder = simplifyBuilder(dialogsListState, {});

export const saveDocument = builder.createServerAction(
  (
    providerId: string,
    sectionId: string,
    documentId: string,
    document: APIDocument,
  ) => ({
    name: 'saveDocumentData',
    url: documentEndpoint(providerId, sectionId, documentId),
    method: httpMethod.put,
    body: () => document,
    //onSuccess: (state: DocumentsState, payload: APIDocumentModel) => (state),
  }),
);

export const loadDocuments = builder.createServerAction(
  (
    providerId: string,
    language: APILanguage,
    sectionId: string,
    filter = {},
  ) => ({
    name: 'loadDocuments',
    url: documentsEndpoint(providerId, sectionId),
    method: httpMethod.post,
    body: () => ({
      pageNumber: 1,
      pageSize: 20,
      searchSortOrders: [
        {
          sortField: 'position',
          sortOrder: APISortOrder.ASC,
        },
      ],
      language: language,
      ...filter,
    }),
    onSuccess: (state: DocumentsState, payload: APIDocumentSearchResult[]) => ({
      documentsLoaded: true,
      items: payload,
    }),
  }),
);

export const resetDocuments = builder.createReduxAction(() => ({
  name: 'resetDocuments',
  updater: () => dialogsListState,
}));

export const useDocuments = () => {
  const dispatch = useDispatch();
  const providerId = useSelector(appSelectors.providerId);
  const userLanguage = useSelector(appSelectors.userLanguage);
  const apiLanguage = mapLanguageToApiLanguage(userLanguage);

  return useMemo(
    () =>
      bindActionCreators(
        {
          loadDocuments: loadDocuments.bind(null, providerId, apiLanguage),
          resetDocuments: resetDocuments.bind(null),
        },
        dispatch,
      ),
    [dispatch, providerId, apiLanguage],
  );
};

export const documentsReducer = builder.getReducers();
